import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { 
  ShieldCheck, Plus, Calendar, FileText, Users, Star, 
  ClipboardCheck, MessageSquare, BarChart3, Trash2, RefreshCw,
  CheckCircle, Clock, AlertCircle, Target, Award
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Map URL paths to tab values
const getTabFromPath = (pathname) => {
  if (pathname.includes('/iqac/initiatives')) return 'initiatives';
  if (pathname.includes('/iqac/feedback')) return 'feedback';
  if (pathname.includes('/iqac/aqar')) return 'aqar';
  return 'meetings';
};

export default function IQACManagement() {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(() => getTabFromPath(location.pathname));
  const [meetings, setMeetings] = useState([]);
  const [initiatives, setInitiatives] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [aqarList, setAqarList] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const [showInitiativeModal, setShowInitiativeModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showAqarModal, setShowAqarModal] = useState(false);
  
  const [meetingForm, setMeetingForm] = useState({
    title: '', meeting_date: '', meeting_type: 'regular', venue: '',
    agenda: '', minutes: '', decisions: '', action_items: '', next_meeting_date: ''
  });
  
  const [initiativeForm, setInitiativeForm] = useState({
    title: '', category: 'teaching_learning', description: '', start_date: '',
    end_date: '', status: 'planned', responsible_person: '', expected_outcome: '',
    actual_outcome: '', budget: '', beneficiaries: ''
  });
  
  const [feedbackForm, setFeedbackForm] = useState({
    feedback_type: 'student', academic_year: '', collection_date: '',
    total_responses: '', average_rating: '', summary: '', key_suggestions: '', actions_taken: ''
  });
  
  const [aqarForm, setAqarForm] = useState({
    academic_year: '', submission_date: '', status: 'draft',
    executive_summary: '', key_achievements: '', areas_of_improvement: '', plans_for_next_year: ''
  });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  // Update tab when URL changes
  useEffect(() => {
    setActiveTab(getTabFromPath(location.pathname));
  }, [location.pathname]);

  useEffect(() => {
    fetchStats();
    fetchData();
  }, [activeTab]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/iqac/stats`, { headers });
      setStats(response.data);
    } catch (err) {
      console.error('Failed to fetch stats');
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'meetings') {
        const res = await axios.get(`${API_URL}/api/iqac/meetings`, { headers });
        setMeetings(res.data.meetings || []);
      } else if (activeTab === 'initiatives') {
        const res = await axios.get(`${API_URL}/api/iqac/initiatives`, { headers });
        setInitiatives(res.data.initiatives || []);
      } else if (activeTab === 'feedback') {
        const res = await axios.get(`${API_URL}/api/iqac/feedback`, { headers });
        setFeedback(res.data.feedback || []);
      } else if (activeTab === 'aqar') {
        const res = await axios.get(`${API_URL}/api/iqac/aqar`, { headers });
        setAqarList(res.data.aqar_reports || []);
      }
    } catch (err) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleMeetingSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...meetingForm,
        agenda: meetingForm.agenda ? meetingForm.agenda.split('\n').filter(a => a.trim()) : [],
        decisions: meetingForm.decisions ? meetingForm.decisions.split('\n').filter(d => d.trim()) : [],
        action_items: meetingForm.action_items ? meetingForm.action_items.split('\n').filter(a => a.trim()) : []
      };
      await axios.post(`${API_URL}/api/iqac/meetings`, payload, { headers });
      toast.success('Meeting created successfully');
      setShowMeetingModal(false);
      resetMeetingForm();
      fetchData();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create meeting');
    }
  };

  const handleInitiativeSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...initiativeForm,
        budget: initiativeForm.budget ? parseFloat(initiativeForm.budget) : null
      };
      await axios.post(`${API_URL}/api/iqac/initiatives`, payload, { headers });
      toast.success('Initiative created successfully');
      setShowInitiativeModal(false);
      resetInitiativeForm();
      fetchData();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create initiative');
    }
  };

  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...feedbackForm,
        total_responses: parseInt(feedbackForm.total_responses),
        average_rating: feedbackForm.average_rating ? parseFloat(feedbackForm.average_rating) : null,
        key_suggestions: feedbackForm.key_suggestions ? feedbackForm.key_suggestions.split('\n').filter(s => s.trim()) : []
      };
      await axios.post(`${API_URL}/api/iqac/feedback`, payload, { headers });
      toast.success('Feedback record created successfully');
      setShowFeedbackModal(false);
      resetFeedbackForm();
      fetchData();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create feedback');
    }
  };

  const handleAqarSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...aqarForm,
        key_achievements: aqarForm.key_achievements ? aqarForm.key_achievements.split('\n').filter(a => a.trim()) : [],
        areas_of_improvement: aqarForm.areas_of_improvement ? aqarForm.areas_of_improvement.split('\n').filter(a => a.trim()) : [],
        plans_for_next_year: aqarForm.plans_for_next_year ? aqarForm.plans_for_next_year.split('\n').filter(p => p.trim()) : []
      };
      await axios.post(`${API_URL}/api/iqac/aqar`, payload, { headers });
      toast.success('AQAR created successfully');
      setShowAqarModal(false);
      resetAqarForm();
      fetchData();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create AQAR');
    }
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm('Are you sure you want to delete this?')) return;
    try {
      await axios.delete(`${API_URL}/api/iqac/${type}/${id}`, { headers });
      toast.success('Deleted successfully');
      fetchData();
      fetchStats();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const resetMeetingForm = () => setMeetingForm({ title: '', meeting_date: '', meeting_type: 'regular', venue: '', agenda: '', minutes: '', decisions: '', action_items: '', next_meeting_date: '' });
  const resetInitiativeForm = () => setInitiativeForm({ title: '', category: 'teaching_learning', description: '', start_date: '', end_date: '', status: 'planned', responsible_person: '', expected_outcome: '', actual_outcome: '', budget: '', beneficiaries: '' });
  const resetFeedbackForm = () => setFeedbackForm({ feedback_type: 'student', academic_year: '', collection_date: '', total_responses: '', average_rating: '', summary: '', key_suggestions: '', actions_taken: '' });
  const resetAqarForm = () => setAqarForm({ academic_year: '', submission_date: '', status: 'draft', executive_summary: '', key_achievements: '', areas_of_improvement: '', plans_for_next_year: '' });

  const statusColors = {
    planned: 'bg-yellow-100 text-yellow-800',
    in_progress: 'bg-blue-100 text-blue-800',
    completed: 'bg-green-100 text-green-800',
    draft: 'bg-gray-100 text-gray-800',
    submitted: 'bg-green-100 text-green-800',
    acknowledged: 'bg-blue-100 text-blue-800'
  };

  const categoryLabels = {
    curriculum: 'Curriculum',
    teaching_learning: 'Teaching & Learning',
    research: 'Research',
    infrastructure: 'Infrastructure',
    governance: 'Governance',
    best_practice: 'Best Practice'
  };

  const StatCard = ({ title, value, icon: Icon, color = 'blue', subtitle }) => (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">{title}</p>
            <p className="text-2xl font-bold">{value}</p>
            {subtitle && <p className="text-xs text-gray-400">{subtitle}</p>}
          </div>
          <div className={`w-12 h-12 bg-${color}-100 rounded-full flex items-center justify-center`}>
            <Icon className={`h-6 w-6 text-${color}-600`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="p-6 space-y-6" data-testid="iqac-management-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">IQAC Management</h1>
          <p className="text-gray-500">Internal Quality Assurance Cell</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => setShowMeetingModal(true)}>
            <Calendar className="h-4 w-4 mr-1" /> Meeting
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowInitiativeModal(true)}>
            <Target className="h-4 w-4 mr-1" /> Initiative
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowFeedbackModal(true)}>
            <MessageSquare className="h-4 w-4 mr-1" /> Feedback
          </Button>
          <Button size="sm" onClick={() => setShowAqarModal(true)}>
            <FileText className="h-4 w-4 mr-1" /> AQAR
          </Button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <StatCard title="Meetings" value={stats.meetings?.total || 0} icon={Calendar} color="blue" />
          <StatCard title="Initiatives" value={stats.initiatives?.total || 0} icon={Target} color="purple" />
          <StatCard title="Completed" value={stats.initiatives?.completed || 0} icon={CheckCircle} color="green" />
          <StatCard title="In Progress" value={stats.initiatives?.in_progress || 0} icon={Clock} color="orange" />
          <StatCard title="Feedback Records" value={stats.feedback?.total || 0} icon={MessageSquare} color="cyan" />
          <StatCard title="AQAR Submitted" value={stats.aqar?.submitted || 0} icon={Award} color="green" />
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="meetings">Meetings</TabsTrigger>
          <TabsTrigger value="initiatives">Initiatives</TabsTrigger>
          <TabsTrigger value="feedback">Feedback</TabsTrigger>
          <TabsTrigger value="aqar">AQAR</TabsTrigger>
        </TabsList>

        {/* Meetings Tab */}
        <TabsContent value="meetings" className="mt-4">
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left">Meeting</th>
                      <th className="px-4 py-3 text-center">Date</th>
                      <th className="px-4 py-3 text-center">Type</th>
                      <th className="px-4 py-3 text-left">Venue</th>
                      <th className="px-4 py-3 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan="5" className="text-center py-8"><RefreshCw className="h-6 w-6 animate-spin mx-auto" /></td></tr>
                    ) : meetings.length === 0 ? (
                      <tr><td colSpan="5" className="text-center py-8 text-gray-500">No meetings found</td></tr>
                    ) : (
                      meetings.map((m) => (
                        <tr key={m.id} className="border-t hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <p className="font-medium">{m.title}</p>
                            {m.decisions?.length > 0 && <p className="text-xs text-gray-500">{m.decisions.length} decisions</p>}
                          </td>
                          <td className="px-4 py-3 text-center">{m.meeting_date}</td>
                          <td className="px-4 py-3 text-center"><Badge variant="outline">{m.meeting_type}</Badge></td>
                          <td className="px-4 py-3">{m.venue || '-'}</td>
                          <td className="px-4 py-3 text-center">
                            <Button variant="ghost" size="sm" onClick={() => handleDelete('meetings', m.id)}>
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Initiatives Tab */}
        <TabsContent value="initiatives" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {loading ? (
              <Card className="col-span-full"><CardContent className="p-8 text-center"><RefreshCw className="h-6 w-6 animate-spin mx-auto" /></CardContent></Card>
            ) : initiatives.length === 0 ? (
              <Card className="col-span-full"><CardContent className="p-8 text-center text-gray-500">No initiatives found</CardContent></Card>
            ) : (
              initiatives.map((init) => (
                <Card key={init.id}>
                  <CardHeader className="pb-2">
                    <div className="flex justify-between items-start">
                      <div>
                        <Badge className="mb-2">{categoryLabels[init.category] || init.category}</Badge>
                        <CardTitle className="text-base">{init.title}</CardTitle>
                      </div>
                      <Badge className={statusColors[init.status]}>{init.status}</Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <p className="text-gray-600 line-clamp-2">{init.description}</p>
                    {init.responsible_person && <p><strong>Responsible:</strong> {init.responsible_person}</p>}
                    <div className="flex justify-between items-center pt-2">
                      <span className="text-gray-500">{init.start_date}</span>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete('initiatives', init.id)}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {/* Feedback Tab */}
        <TabsContent value="feedback" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {feedback.length === 0 ? (
              <Card className="col-span-full"><CardContent className="p-8 text-center text-gray-500">No feedback records found</CardContent></Card>
            ) : (
              feedback.map((fb) => (
                <Card key={fb.id}>
                  <CardHeader className="pb-2">
                    <div className="flex justify-between items-start">
                      <div>
                        <Badge className="capitalize">{fb.feedback_type}</Badge>
                        <CardTitle className="text-base mt-2">{fb.academic_year}</CardTitle>
                      </div>
                      {fb.average_rating && (
                        <div className="flex items-center gap-1 bg-yellow-100 px-2 py-1 rounded">
                          <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                          <span className="font-bold">{fb.average_rating}</span>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <p><strong>Responses:</strong> {fb.total_responses}</p>
                    {fb.summary && <p className="text-gray-600">{fb.summary}</p>}
                    <div className="flex justify-between items-center pt-2">
                      <span className="text-gray-500">{fb.collection_date}</span>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete('feedback', fb.id)}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {/* AQAR Tab */}
        <TabsContent value="aqar" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {aqarList.length === 0 ? (
              <Card className="col-span-full"><CardContent className="p-8 text-center text-gray-500">No AQAR reports found</CardContent></Card>
            ) : (
              aqarList.map((aqar) => (
                <Card key={aqar.id}>
                  <CardHeader className="pb-2">
                    <div className="flex justify-between items-start">
                      <CardTitle className="text-lg">{aqar.academic_year}</CardTitle>
                      <Badge className={statusColors[aqar.status]}>{aqar.status}</Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    {aqar.submission_date && <p><strong>Submitted:</strong> {aqar.submission_date}</p>}
                    {aqar.key_achievements?.length > 0 && (
                      <p><strong>Achievements:</strong> {aqar.key_achievements.length} items</p>
                    )}
                    <div className="flex justify-end pt-2">
                      <Button variant="ghost" size="sm" onClick={() => handleDelete('aqar', aqar.id)}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Meeting Modal */}
      <Dialog open={showMeetingModal} onOpenChange={setShowMeetingModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Add IQAC Meeting</DialogTitle></DialogHeader>
          <form onSubmit={handleMeetingSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium">Meeting Title *</label>
              <Input value={meetingForm.title} onChange={(e) => setMeetingForm({...meetingForm, title: e.target.value})} required />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Date *</label>
                <Input type="date" value={meetingForm.meeting_date} onChange={(e) => setMeetingForm({...meetingForm, meeting_date: e.target.value})} required />
              </div>
              <div>
                <label className="text-sm font-medium">Type</label>
                <Select value={meetingForm.meeting_type} onValueChange={(v) => setMeetingForm({...meetingForm, meeting_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="regular">Regular</SelectItem>
                    <SelectItem value="special">Special</SelectItem>
                    <SelectItem value="emergency">Emergency</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Venue</label>
              <Input value={meetingForm.venue} onChange={(e) => setMeetingForm({...meetingForm, venue: e.target.value})} />
            </div>
            <div>
              <label className="text-sm font-medium">Agenda (one per line)</label>
              <Textarea value={meetingForm.agenda} onChange={(e) => setMeetingForm({...meetingForm, agenda: e.target.value})} rows={3} />
            </div>
            <div>
              <label className="text-sm font-medium">Decisions (one per line)</label>
              <Textarea value={meetingForm.decisions} onChange={(e) => setMeetingForm({...meetingForm, decisions: e.target.value})} rows={3} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowMeetingModal(false)}>Cancel</Button>
              <Button type="submit">Create Meeting</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Initiative Modal */}
      <Dialog open={showInitiativeModal} onOpenChange={setShowInitiativeModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Add Quality Initiative</DialogTitle></DialogHeader>
          <form onSubmit={handleInitiativeSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium">Title *</label>
              <Input value={initiativeForm.title} onChange={(e) => setInitiativeForm({...initiativeForm, title: e.target.value})} required />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Category *</label>
                <Select value={initiativeForm.category} onValueChange={(v) => setInitiativeForm({...initiativeForm, category: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="curriculum">Curriculum</SelectItem>
                    <SelectItem value="teaching_learning">Teaching & Learning</SelectItem>
                    <SelectItem value="research">Research</SelectItem>
                    <SelectItem value="infrastructure">Infrastructure</SelectItem>
                    <SelectItem value="governance">Governance</SelectItem>
                    <SelectItem value="best_practice">Best Practice</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Status</label>
                <Select value={initiativeForm.status} onValueChange={(v) => setInitiativeForm({...initiativeForm, status: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="planned">Planned</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Description *</label>
              <Textarea value={initiativeForm.description} onChange={(e) => setInitiativeForm({...initiativeForm, description: e.target.value})} required />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Start Date *</label>
                <Input type="date" value={initiativeForm.start_date} onChange={(e) => setInitiativeForm({...initiativeForm, start_date: e.target.value})} required />
              </div>
              <div>
                <label className="text-sm font-medium">End Date</label>
                <Input type="date" value={initiativeForm.end_date} onChange={(e) => setInitiativeForm({...initiativeForm, end_date: e.target.value})} />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Responsible Person</label>
              <Input value={initiativeForm.responsible_person} onChange={(e) => setInitiativeForm({...initiativeForm, responsible_person: e.target.value})} />
            </div>
            <div>
              <label className="text-sm font-medium">Expected Outcome</label>
              <Textarea value={initiativeForm.expected_outcome} onChange={(e) => setInitiativeForm({...initiativeForm, expected_outcome: e.target.value})} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowInitiativeModal(false)}>Cancel</Button>
              <Button type="submit">Create Initiative</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Feedback Modal */}
      <Dialog open={showFeedbackModal} onOpenChange={setShowFeedbackModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Add Feedback Record</DialogTitle></DialogHeader>
          <form onSubmit={handleFeedbackSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Feedback Type *</label>
                <Select value={feedbackForm.feedback_type} onValueChange={(v) => setFeedbackForm({...feedbackForm, feedback_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="student">Student</SelectItem>
                    <SelectItem value="alumni">Alumni</SelectItem>
                    <SelectItem value="employer">Employer</SelectItem>
                    <SelectItem value="parent">Parent</SelectItem>
                    <SelectItem value="teacher">Teacher</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Academic Year *</label>
                <Input value={feedbackForm.academic_year} onChange={(e) => setFeedbackForm({...feedbackForm, academic_year: e.target.value})} placeholder="2023-24" required />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Collection Date *</label>
                <Input type="date" value={feedbackForm.collection_date} onChange={(e) => setFeedbackForm({...feedbackForm, collection_date: e.target.value})} required />
              </div>
              <div>
                <label className="text-sm font-medium">Total Responses *</label>
                <Input type="number" value={feedbackForm.total_responses} onChange={(e) => setFeedbackForm({...feedbackForm, total_responses: e.target.value})} required />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Average Rating (out of 5)</label>
              <Input type="number" step="0.1" max="5" value={feedbackForm.average_rating} onChange={(e) => setFeedbackForm({...feedbackForm, average_rating: e.target.value})} />
            </div>
            <div>
              <label className="text-sm font-medium">Summary</label>
              <Textarea value={feedbackForm.summary} onChange={(e) => setFeedbackForm({...feedbackForm, summary: e.target.value})} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowFeedbackModal(false)}>Cancel</Button>
              <Button type="submit">Add Feedback</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* AQAR Modal */}
      <Dialog open={showAqarModal} onOpenChange={setShowAqarModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Create AQAR</DialogTitle></DialogHeader>
          <form onSubmit={handleAqarSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Academic Year *</label>
                <Input value={aqarForm.academic_year} onChange={(e) => setAqarForm({...aqarForm, academic_year: e.target.value})} placeholder="2023-24" required />
              </div>
              <div>
                <label className="text-sm font-medium">Status</label>
                <Select value={aqarForm.status} onValueChange={(v) => setAqarForm({...aqarForm, status: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="submitted">Submitted</SelectItem>
                    <SelectItem value="acknowledged">Acknowledged</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Submission Date</label>
              <Input type="date" value={aqarForm.submission_date} onChange={(e) => setAqarForm({...aqarForm, submission_date: e.target.value})} />
            </div>
            <div>
              <label className="text-sm font-medium">Executive Summary</label>
              <Textarea value={aqarForm.executive_summary} onChange={(e) => setAqarForm({...aqarForm, executive_summary: e.target.value})} rows={3} />
            </div>
            <div>
              <label className="text-sm font-medium">Key Achievements (one per line)</label>
              <Textarea value={aqarForm.key_achievements} onChange={(e) => setAqarForm({...aqarForm, key_achievements: e.target.value})} rows={3} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAqarModal(false)}>Cancel</Button>
              <Button type="submit">Create AQAR</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
